import { useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts'
import {
  ArrowRight,
  Banknote,
  CreditCard,
  Gift,
  BookUser,
  Globe,
  Receipt,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import apiClient from '@/api/client'
import { useCurrency } from '@/contexts/CurrencyContext'
import { rangeToSearch, type UseReportRange } from '@/hooks/useReportRange'
import type { OverviewReport, DailySalesRow } from '@/types'
import { metricPairOrEmpty } from '@/components/admin/dashboard/dashboardFormat'
import { ReportsHeadline, type ReportTarget } from './ReportsHeadline'
import { cogsBasisDisplay } from '@/components/finance/cogsBasisDisplay'
import { ExportButton } from './ExportButton'
import { ReportsExportSlot } from './ReportsExportSlot'
import { openPrintableReport, escapeHtml } from '@/lib/printReport'
import { useBusinessNameWithFallback } from '@/hooks/useBusinessName'
import { formatDateHuman, formatDateRangeHuman, parseIsoDate } from '@/lib/utils'
import {
  displayLabelForOrderType,
  colorForOrderTypeId,
  type OrderTypeConfig,
} from '@/lib/orderTypeSettings'
import { useEnabledOrderTypes } from '@/hooks/useEnabledOrderTypes'
import { cn } from '@/lib/utils'
import type { ReportsSearch } from './reportsRouteSearch'
import { chartTooltip, chartTooltipPie } from '@/lib/chartTheme'

const TENDER_COLORS: Record<string, string> = {
  cash: '#22c55e',
  card: '#3b82f6',
  online: '#a855f7',
  // F-MM-N24: its own swatch rather than the grey "other" fallback, so the
  // gift slice is legible in the donut against the three money tenders.
  gift_card: '#f59e0b',
  house_account: '#0d9488',
  other: '#9ca3af',
}

// Fallback swatches for the order-type donut/breakdown when the tenant hasn't
// set a custom colour on a type. Mirrors OrderTypeMixBar's TYPE_COLORS (as hex,
// since Recharts Cell needs a colour value, not a Tailwind class).
const ORDER_TYPE_COLORS: Record<string, string> = {
  dine_in: '#22c55e',
  takeout: '#f97316',
  takeaway: '#f97316',
  delivery: '#3b82f6',
  counter: '#10b981',
  other: '#9ca3af',
}

function orderTypeColorFor(id: string, rows: OrderTypeConfig[]): string {
  return colorForOrderTypeId(rows, id) ?? ORDER_TYPE_COLORS[id] ?? ORDER_TYPE_COLORS.other
}

function tenderColor(method: string): string {
  return TENDER_COLORS[method] ?? TENDER_COLORS.other
}

function tenderIcon(method: string) {
  switch (method) {
    case 'cash':
      return <Banknote className="w-3.5 h-3.5" />
    case 'card':
      return <CreditCard className="w-3.5 h-3.5" />
    case 'online':
      return <Globe className="w-3.5 h-3.5" />
    // F-MM-N24: its own mark, so the gift slice is distinguishable from the
    // generic "other" bucket in the donut and the breakdown.
    case 'gift_card':
      return <Gift className="w-3.5 h-3.5" />
    case 'house_account':
      return <BookUser className="w-3.5 h-3.5" />
    default:
      return <Receipt className="w-3.5 h-3.5" />
  }
}

function tenderLabel(method: string): string {
  switch (method) {
    case 'cash':
      return 'Cash'
    case 'card':
      return 'Card'
    case 'online':
      return 'Online'
    // F-MM-N24. Named explicitly: the default branch only capitalises the
    // first letter, so the raw enum reached the donut legend and the
    // breakdown as "Gift_card".
    case 'gift_card':
      return 'Gift card'
    // F-MM-N30: tabs charged today (staff meals are not revenue, so they
    // never reach the tender mix).
    case 'house_account':
      return 'On tab'
    default:
      return method.charAt(0).toUpperCase() + method.slice(1)
  }
}

interface Props {
  range: UseReportRange
}

export function OverviewTab({ range }: Props) {
  const { formatCurrency } = useCurrency()

  const overviewQuery = useQuery({
    queryKey: ['reports-v2', 'overview', range.fromISO, range.toISO],
    queryFn: () => apiClient.getReportsOverview(range.fromISO, range.toISO),
    staleTime: 30_000,
  })

  const dailyQuery = useQuery({
    queryKey: ['reports-v2', 'daily', range.fromISO, range.toISO],
    queryFn: () => apiClient.getDailySalesReport(range.fromISO, range.toISO),
    staleTime: 30_000,
  })

  // Order-type config so custom channels (e.g. "Foodpanda") resolve to their
  // real labels/colours in the order-type donut + breakdown.
  const { raw: orderTypeRows } = useEnabledOrderTypes()

  // F-RPT-N30 — every breakdown row links into the Orders browser with that
  // facet armed. A single-day range opens the day drawer directly; a longer
  // range lands on Daily Sales with the filter armed for whichever day the
  // operator opens (until the range-wide Orders tab ships).
  const navigate = useNavigate()
  const singleDay = range.fromISO === range.toISO
  const openOrders = (facet: Pick<ReportsSearch, 'ot' | 'pay' | 'sub'>) => {
    navigate({
      to: '/admin/reports',
      search: (prev): ReportsSearch => ({
        ...(prev as ReportsSearch),
        // F-FE-N149: the router never saw the period (raw replaceState),
        // so re-stamp it or this navigation strips it from the URL.
        ...rangeToSearch(range),
        tab: 'daily',
        day: singleDay ? range.toISO : undefined,
        order: undefined,
        ot: undefined,
        pay: undefined,
        sub: undefined,
        st: undefined,
        ...facet,
      }),
    })
  }
  const drillHint = singleDay ? 'View orders' : 'Filter Daily sales'

  const overview = overviewQuery.data?.data
  const daily = dailyQuery.data?.data ?? []

  // Defensive: Go's `encoding/json` serializes nil slices as `null`, so any
  // empty-result array on `overview` may arrive as null from the API. Normalize
  // here once so the rest of the component can treat it as an array.
  const tenderMix = overview?.tender_mix ?? []
  // Online-provider breakdown (migration 050). Empty unless the tenant enabled
  // online sub-methods and attributed at least one online sale.
  const onlineSubMix = overview?.tender_mix_online_submethods ?? []
  // Sales segmented by order type, net of refunds (sums to gross-after-refunds).
  const orderTypeMix = overview?.order_type_mix ?? []

  const comparisonLabel = useMemo(() => {
    if (!overview) return 'vs. previous period'
    const prevFrom = parseIsoDate(overview.previous_from)
    const prevTo = parseIsoDate(overview.previous_to)
    if (!prevFrom || !prevTo) return 'vs. previous period'

    // Phrase the comparison relative to the active preset so the badge
    // reads as English rather than as two repeated dates. Falls back to
    // a generic "previous period" label when the preset is custom.
    const previousLabel = formatDateRangeHuman(prevFrom, prevTo)
    switch (range.preset) {
      case 'today':
        return `vs. yesterday (${previousLabel})`
      case 'yesterday':
        return `vs. day before (${previousLabel})`
      case 'last_7':
        return `vs. previous 7 days (${previousLabel})`
      case 'last_30':
        return `vs. previous 30 days (${previousLabel})`
      case 'this_month':
        return `vs. last month (${previousLabel})`
      case 'last_month':
        return `vs. month before (${previousLabel})`
      default:
        return `vs. previous period (${previousLabel})`
    }
  }, [overview, range.preset])

  // F-FE-N204: the headline's comparison, short enough to sit beside the
  // change ("vs Fri"); the full window stays in the hover text.
  const comparisonShort = useMemo(() => {
    const prevFrom = overview ? parseIsoDate(overview.previous_from) : null
    switch (range.preset) {
      case 'today':
      case 'yesterday':
        return prevFrom ? `vs ${prevFrom.toLocaleDateString(undefined, { weekday: 'short' })}` : 'vs day before'
      case 'last_7':
        return 'vs previous 7 days'
      case 'last_30':
        return 'vs previous 30 days'
      case 'this_month':
        return 'vs last month'
      case 'last_month':
        return 'vs month before'
      default:
        return 'vs previous period'
    }
  }, [overview, range.preset])

  // F-FE-N204: every headline figure opens the report behind it, same period.
  const openReport = (target: ReportTarget) => {
    switch (target) {
      case 'orders':
        openOrders({})
        return
      case 'pnl':
      case 'ledger':
        navigate({ to: '/admin/expenses', search: { tab: target, ...rangeToSearch(range) } })
        return
      default:
        navigate({
          to: '/admin/reports',
          search: (prev): ReportsSearch => ({ ...(prev as ReportsSearch), ...rangeToSearch(range), tab: target }),
        })
    }
  }

  const tenderTotal = tenderMix.reduce((s, r) => s + r.amount, 0)
  const orderTypeTotal = orderTypeMix.reduce((s, r) => s + r.amount, 0)

  const brand = useBusinessNameWithFallback()
  const handlePrintPdf = () => {
    if (!overview) return
    openPrintableReport({
      title: 'Sales Overview',
      subtitle: `${formatDateRangeHuman(range.from, range.to)} • ${overview.timezone}`,
      bodyHtml: buildOverviewPdf(overview, daily, formatCurrency),
      brand,
    })
  }

  return (
    <div className="space-y-6">
      <ReportsExportSlot>
        <ExportButton
          report="overview"
          reportLabel="Overview"
          fromISO={range.fromISO}
          toISO={range.toISO}
          onPrintPdf={handlePrintPdf}
          onPeriodPack={() => apiClient.exportReportXlsx('period_pack', range.fromISO, range.toISO)}
        />
      </ReportsExportSlot>

      <p className="text-xs text-muted-foreground">
        Showing data for{' '}
        <span className="font-medium text-foreground">
          {formatDateRangeHuman(range.from, range.to)}
        </span>
        . All numbers below cover this window.
      </p>

      {overviewQuery.isError && (
        <Card className="border-destructive/50">
          <CardContent className="p-4 text-sm text-destructive">
            Failed to load overview: {String((overviewQuery.error as Error)?.message ?? 'unknown')}
          </CardContent>
        </Card>
      )}

      {overviewQuery.isLoading || !overview ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-5 space-y-3">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-7 w-32" />
                <Skeleton className="h-4 w-40" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <ReportsHeadline
          overview={overview}
          comparisonShort={comparisonShort}
          comparisonFull={comparisonLabel}
          onOpen={openReport}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Net sales trend</CardTitle>
          </CardHeader>
          <CardContent className="h-[280px]">
            {dailyQuery.isLoading ? (
              <Skeleton className="w-full h-full" />
            ) : daily.length === 0 ? (
              <EmptyState message="No sales in the selected range" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={daily} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                    tickFormatter={(iso: string) => formatDateHuman(iso)}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => formatCompact(v)}
                    width={60}
                  />
                  <ReTooltip
                    {...chartTooltip}
                    formatter={(v: number) => [formatCurrency(v), 'Net sales']}
                  />
                  <Line type="monotone" dataKey="net" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tender mix</CardTitle>
          </CardHeader>
          <CardContent className="h-[280px]">
            {overviewQuery.isLoading ? (
              <Skeleton className="w-full h-full" />
            ) : !overview || tenderMix.length === 0 || tenderTotal === 0 ? (
              <EmptyState message="No payments yet" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={tenderMix}
                    dataKey="amount"
                    nameKey="method"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {tenderMix.map((entry) => (
                      <Cell key={entry.method} fill={tenderColor(entry.method)} />
                    ))}
                  </Pie>
                  <ReTooltip
                    {...chartTooltipPie}
                    formatter={(v: number, name: string) => [formatCurrency(v), tenderLabel(String(name))]}
                  />
                  <Legend
                    verticalAlign="bottom"
                    iconSize={8}
                    formatter={(value) => (
                      <span className="text-xs">{tenderLabel(String(value))}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {overview && tenderMix.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tender mix breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {tenderMix.map((row) => (
              <div key={row.method} className="space-y-1.5">
                <DrillRow
                  onClick={() => openOrders({ pay: tenderFacet(row.method) })}
                  hint={drillHint}
                  left={
                    <>
                      <span style={{ color: tenderColor(row.method) }}>{tenderIcon(row.method)}</span>
                      <span className="font-medium">{tenderLabel(row.method)}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {row.count} order{row.count === 1 ? '' : 's'}
                      </Badge>
                    </>
                  }
                  pct={row.pct}
                  amount={formatCurrency(row.amount)}
                />
                {/* Online provider sub-breakdown (migration 050) — indented under
                    the online row; amounts reconcile to the online total. */}
                {row.method === 'online' &&
                  onlineSubMix.map((sub) => (
                    <DrillRow
                      key={sub.slug || 'unattributed'}
                      nested
                      // Unattributed online sales have no slug to filter on;
                      // the row still opens the online list.
                      onClick={() => openOrders({ pay: 'online', sub: sub.slug || undefined })}
                      hint={drillHint}
                      left={
                        <>
                          <span className="text-muted-foreground">↳</span>
                          <span className="font-medium">{sub.label}</span>
                          <Badge variant="outline" className="text-[10px]">
                            {sub.count} order{sub.count === 1 ? '' : 's'}
                          </Badge>
                        </>
                      }
                      pct={sub.pct}
                      amount={formatCurrency(sub.amount)}
                    />
                  ))}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {overview && orderTypeMix.length > 0 && orderTypeTotal > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">Order type mix breakdown</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {orderTypeMix.map((row) => (
                <DrillRow
                  key={row.order_type}
                  onClick={() => openOrders({ ot: row.order_type })}
                  hint={drillHint}
                  left={
                    <>
                      <span
                        className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                        style={{ backgroundColor: orderTypeColorFor(row.order_type, orderTypeRows) }}
                      />
                      <span className="font-medium">
                        {displayLabelForOrderType(row.order_type, orderTypeRows)}
                      </span>
                      <Badge variant="outline" className="text-[10px]">
                        {row.count} order{row.count === 1 ? '' : 's'}
                      </Badge>
                    </>
                  }
                  pct={row.pct}
                  amount={formatCurrency(row.amount)}
                />
              ))}
              <p className="pt-1 text-[11px] text-muted-foreground">
                Amounts are net of refunds and tie out to Gross sales after refunds.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Order type mix</CardTitle>
            </CardHeader>
            <CardContent className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={orderTypeMix}
                    dataKey="amount"
                    nameKey="order_type"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {orderTypeMix.map((entry) => (
                      <Cell
                        key={entry.order_type}
                        fill={orderTypeColorFor(entry.order_type, orderTypeRows)}
                      />
                    ))}
                  </Pie>
                  <ReTooltip
                    {...chartTooltipPie}
                    formatter={(v: number, name: string) => [
                      formatCurrency(v),
                      displayLabelForOrderType(String(name), orderTypeRows),
                    ]}
                  />
                  <Legend
                    verticalAlign="bottom"
                    iconSize={8}
                    formatter={(value) => (
                      <span className="text-xs">
                        {displayLabelForOrderType(String(value), orderTypeRows)}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

function tenderFacet(method: string): 'cash' | 'card' | 'online' | 'gift_card' | 'tab' | undefined {
  // The "On tab" row is tab bills only; `house_account` would also list staff meals.
  if (method === 'house_account') return 'tab'
  // F-MM-N24 added gift_card. Without it the gift row's "View orders" drill
  // passed no filter at all and quietly listed the whole day — the one bill a
  // guest is most likely to query is the one the filter refused to find. The
  // backend orders browser accepts the same four values.
  return method === 'cash' || method === 'card' || method === 'online' || method === 'gift_card'
    ? method
    : undefined
}

/**
 * One breakdown row that drills into the Orders browser. Rendered as a real
 * button (keyboard reachable) with a trailing "View orders" affordance so it
 * reads as a link, not decoration.
 */
function DrillRow({
  left,
  pct,
  amount,
  hint,
  onClick,
  nested = false,
}: {
  left: React.ReactNode
  pct: number
  amount: string
  hint: string
  onClick: () => void
  nested?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group flex w-full items-center justify-between gap-3 rounded-md border text-left transition',
        'hover:border-foreground/30 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        nested
          ? 'ml-6 w-[calc(100%-1.5rem)] border-dashed border-border/70 bg-muted/10 px-3 py-2 text-xs'
          : 'border-border bg-muted/20 p-3 text-sm',
      )}
    >
      <div className="flex items-center gap-2">{left}</div>
      <div className="flex items-center gap-4 tabular-nums">
        <span className="text-muted-foreground">{pct.toFixed(1)}%</span>
        <span className="font-semibold">{amount}</span>
        <span className="hidden items-center gap-1 text-[11px] font-medium text-muted-foreground opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100 sm:inline-flex">
          {hint} <ArrowRight className="h-3 w-3" />
        </span>
      </div>
    </button>
  )
}

function formatCompact(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return String(value)
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
      {message}
    </div>
  )
}

function buildOverviewPdf(
  overview: OverviewReport,
  daily: DailySalesRow[],
  formatCurrency: (n: number) => string,
): string {
  const basis = cogsBasisDisplay(overview.cogs_basis)
  const marginPct = metricPairOrEmpty(overview.projected_margin_pct).current.toFixed(1)
  const kpis = [
    ['Net Sales', formatCurrency(overview.net_sales.current)],
    ['Discounts', formatCurrency(overview.discounts.current)],
    ['Tax', formatCurrency(overview.tax.current)],
    ['Service Charge', formatCurrency(overview.service_charge.current)],
    ['Orders', overview.orders.current.toLocaleString('en-US')],
    ['Guests', overview.covers.current.toLocaleString('en-US')],
    ['Avg Order Value', formatCurrency(overview.average_check.current)],
    ['Refunds Issued', formatCurrency(metricPairOrEmpty(overview.refunds_issued).current)],
    ['Gross Sales After Refunds', formatCurrency(metricPairOrEmpty(overview.gross_sales_after_refunds).current)],
    [
      basis.purchases ? 'Margin' : 'Projected Margin',
      basis.showCoverage
        ? `${formatCurrency(metricPairOrEmpty(overview.projected_margin).current)} (${marginPct}%, ${metricPairOrEmpty(overview.projected_coverage_pct).current.toFixed(0)}% covered)`
        : `${formatCurrency(metricPairOrEmpty(overview.projected_margin).current)} (${marginPct}%)`,
    ],
    ['Net Profit', formatCurrency(metricPairOrEmpty(overview.net_profit).current)],
  ]
  const kvHtml = kpis
    .map(
      ([label, value]) =>
        `<div><span class="label">${escapeHtml(label)}</span><span class="value">${escapeHtml(value)}</span></div>`,
    )
    .join('')
  const tenderRows = (overview.tender_mix ?? [])
    .map(
      (t) =>
        `<tr><td>${escapeHtml(tenderLabel(t.method))}</td><td class="num">${t.count}</td><td class="num">${escapeHtml(formatCurrency(t.amount))}</td><td class="num">${t.pct.toFixed(1)}%</td></tr>`,
    )
    .join('')
  const dailyRows = daily
    .map(
      (d) =>
        `<tr><td>${escapeHtml(formatDateHuman(d.date))}</td><td class="num">${d.orders}</td><td class="num">${d.covers}</td><td class="num">${escapeHtml(formatCurrency(d.net))}</td></tr>`,
    )
    .join('')

  return `
    <section><div class="kv">${kvHtml}</div></section>
    <section><div class="section-title">Tender mix</div>
      <table><thead><tr><th>Method</th><th class="num">Orders</th><th class="num">Amount</th><th class="num">% of total</th></tr></thead>
        <tbody>${tenderRows || `<tr><td colspan="4" class="muted">No payments</td></tr>`}</tbody></table>
    </section>
    <section><div class="section-title">Daily sales</div>
      <table><thead><tr><th>Date</th><th class="num">Orders</th><th class="num">Guests</th><th class="num">Net</th></tr></thead>
        <tbody>${dailyRows || `<tr><td colspan="4" class="muted">No sales</td></tr>`}</tbody></table>
    </section>
  `
}
